import { use, useCallback, useEffect, useId, useState } from 'react';

import { MediaContext } from '../context/MediaContext';
import { MediaContextActionType, MediaContextPlaybackStatus } from '../context/MediaContext';

type UseMediaContextOptions = (status: MediaContextPlaybackStatus) => void;

/**
 * Tracks the media playback state across the entire application. This coordinates between al media instances
 * such that only one media instance is playing at a time. If another instance starts playing the state of the
 * other instances will be reset correctly.
 *
 * @param options
 * @returns
 */
export function useMediaState(onMediaStatusChange?: UseMediaContextOptions) {
	const [instanceId] = useState<string>(`media_id_${useId()}`);

	const {
		dispatch,
		state: { currentMediaId, mediaPlaybackStatus },
	} = use(MediaContext);

	const mediaPause = useCallback(() => {
		if (!!dispatch) {
			dispatch({ type: MediaContextActionType.Pause, mediaId: instanceId });
			onMediaStatusChange?.(MediaContextPlaybackStatus.PAUSED);
		}
	}, [dispatch, instanceId, onMediaStatusChange]);

	const mediaStart = useCallback(() => {
		if (!!dispatch) {
			dispatch({ type: MediaContextActionType.Start, mediaId: instanceId });

			onMediaStatusChange?.(MediaContextPlaybackStatus.STARTED);
		}
	}, [dispatch, instanceId, onMediaStatusChange]);

	const mediaStop = useCallback(() => {
		if (!!dispatch) {
			dispatch({ type: MediaContextActionType.Stop, mediaId: instanceId });
			onMediaStatusChange?.(MediaContextPlaybackStatus.STOPPED);
		}
	}, [dispatch, instanceId, onMediaStatusChange]);

	useEffect(() => {
		return () => {
			if (!!dispatch && currentMediaId === instanceId) {
				dispatch({ type: MediaContextActionType.Reset, mediaId: instanceId });
			}
			onMediaStatusChange?.(MediaContextPlaybackStatus.STOPPED);
		};
		// when the component unmounts and the current media ist set to the current instance reset the state.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		mediaPlaybackStatus: currentMediaId === instanceId ? mediaPlaybackStatus : MediaContextPlaybackStatus.STOPPED,
		mediaPause,
		mediaStart,
		mediaStop,
	};
}
